import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../../../hooks/useAuth";
import { useCreateProviderPricingItem } from "../../provider-owner.hooks";

const SERVICE_CATEGORIES = [
  "Hair",
  "Barbering",
  "Makeup",
  "Nails",
  "Brows & lashes",
  "Skincare & facials",
  "Spa & body care",
  "Tattoo & body art",
  "Permanent makeup",
  "Piercing",
  "Aesthetics & cosmetic beauty",
] as const;

const SERVICE_SUBCATEGORIES: Record<string, readonly string[]> = {
  Hair: ["Haircut", "Styling", "Colouring", "Treatment", "Extensions"],
  Barbering: ["Haircut", "Beard trim", "Shave", "Fade", "Grooming package"],
  Makeup: ["Party makeup", "Bridal makeup", "Trial", "Editorial makeup"],
  Nails: ["Manicure", "Pedicure", "Gel nails", "Acrylic nails", "Nail art"],
  "Brows & lashes": [
    "Threading",
    "Tinting",
    "Brow lamination",
    "Lash lift",
    "Lash extensions",
  ],
  "Skincare & facials": ["Facial", "Peel", "Cleanup", "Dermaplaning"],
  "Spa & body care": ["Body polish", "Waxing", "Massage", "Body wrap"],
  "Tattoo & body art": [
    "Consultation",
    "Fine line",
    "Realism",
    "Cover-up",
    "Sleeve session",
    "Touch-up",
  ],
  "Permanent makeup": ["Brows", "Lips", "Eyeliner", "Correction", "Touch-up"],
  Piercing: [
    "Ear piercing",
    "Nose piercing",
    "Body piercing",
    "Jewellery change",
    "Aftercare consult",
  ],
  "Aesthetics & cosmetic beauty": [
    "Consultation",
    "Skin booster",
    "Chemical peel",
    "Laser beauty service",
    "Injectable consultation",
  ],
};

const COMPLIANCE_SENSITIVE_SERVICE_CATEGORIES = new Set<string>([
  "Tattoo & body art",
  "Permanent makeup",
  "Piercing",
  "Aesthetics & cosmetic beauty",
]);

const cardStyle: React.CSSProperties = {
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: 18,
  padding: "1rem",
};

const gridStyle: React.CSSProperties = {
  display: "grid",
  gap: "1rem",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
};

const labelStyle: React.CSSProperties = {
  display: "grid",
  gap: "0.45rem",
  color: "#334155",
  fontWeight: 600,
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  border: "1px solid #d1d5db",
  borderRadius: 12,
  padding: "0.85rem 0.95rem",
  background: "#fff",
  color: "#0f172a",
};

const buttonStyle: React.CSSProperties = {
  border: "none",
  borderRadius: 12,
  background: "#111827",
  color: "#fff",
  padding: "0.85rem 1rem",
  fontWeight: 700,
  cursor: "pointer",
};

const secondaryButtonStyle: React.CSSProperties = {
  textDecoration: "none",
  border: "1px solid #d1d5db",
  borderRadius: 12,
  padding: "0.85rem 1rem",
  color: "#111827",
  fontWeight: 700,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

const helperStyle: React.CSSProperties = {
  margin: "0.45rem 0 0",
  color: "#64748b",
  lineHeight: 1.6,
  fontSize: "0.92rem",
};

const complianceHintStyle: React.CSSProperties = {
  border: "1px solid #fde68a",
  background: "#fffbeb",
  color: "#92400e",
  borderRadius: 12,
  padding: "0.9rem 1rem",
  fontSize: "0.92rem",
  lineHeight: 1.6,
};

export default function ProviderNewServicePage() {
  const { user } = useAuth();
  const providerId = user?.providerId ?? "";

  const createPricingItem = useCreateProviderPricingItem(providerId);

  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [form, setForm] = useState({
    serviceName: "",
    serviceCode: "",
    serviceCategory: "",
    serviceSubcategory: "",
    durationMinutes: "60",
    price: "",
    depositRequired: "no",
    depositAmount: "",
    notes: "",
  });

  const availableSubcategories = useMemo(() => {
    return SERVICE_SUBCATEGORIES[form.serviceCategory] ?? [];
  }, [form.serviceCategory]);

  const isComplianceSensitive = COMPLIANCE_SENSITIVE_SERVICE_CATEGORIES.has(
    form.serviceCategory,
  );

  const updateField =
    (field: keyof typeof form) =>
    (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
    ) => {
      const value = event.target.value;

      setForm((current) => {
        if (field === "serviceCategory") {
          return {
            ...current,
            serviceCategory: value,
            serviceSubcategory: "",
          };
        }

        return { ...current, [field]: value };
      });
    };

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!providerId) return;

    const payload = {
      serviceName: form.serviceName.trim(),
      category: form.serviceCategory.trim(),
      price: Number(form.price),
      durationMinutes: Number(form.durationMinutes),
      depositAmount:
        form.depositRequired === "yes" && form.depositAmount.trim()
          ? Number(form.depositAmount)
          : null,
      isActive: true,
    };

    await createPricingItem.mutateAsync(payload);
    setSavedAt(new Date().toLocaleTimeString());
  };

  if (!providerId) {
    return (
      <div style={cardStyle}>
        <h2 style={{ margin: 0, color: "#0f172a" }}>New service</h2>
        <p style={{ marginTop: "0.5rem", color: "#b91c1c" }}>
          No provider is linked to this account yet. Please contact support.
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gap: "1rem" }}>
      <section style={cardStyle}>
        <h2 style={{ margin: 0, color: "#0f172a" }}>New service</h2>
        <p style={{ margin: "0.5rem 0 0", color: "#475569", lineHeight: 1.7 }}>
          Add a provider service with a normalized HnB category, a provider-defined
          service name, pricing, duration, and deposit behavior.
        </p>
      </section>

      <form onSubmit={handleSave} style={{ display: "grid", gap: "1rem" }}>
        <section style={cardStyle}>
          <div style={gridStyle}>
            <label style={labelStyle}>
              Service category
              <select
                style={inputStyle}
                value={form.serviceCategory}
                onChange={updateField("serviceCategory")}
              >
                <option value="">Select category</option>
                {SERVICE_CATEGORIES.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </label>

            <label style={labelStyle}>
              Service subcategory
              <select
                style={inputStyle}
                value={form.serviceSubcategory}
                onChange={updateField("serviceSubcategory")}
                disabled={!form.serviceCategory}
              >
                <option value="">
                  {form.serviceCategory
                    ? "Select subcategory"
                    : "Choose category first"}
                </option>
                {availableSubcategories.map((subcategory) => (
                  <option key={subcategory} value={subcategory}>
                    {subcategory}
                  </option>
                ))}
              </select>
            </label>

            <label style={labelStyle}>
              Service name
              <input
                style={inputStyle}
                value={form.serviceName}
                onChange={updateField("serviceName")}
                placeholder="Fine line wrist tattoo, up to 5 cm"
              />
            </label>

            <label style={labelStyle}>
              Service code
              <input
                style={inputStyle}
                value={form.serviceCode}
                onChange={updateField("serviceCode")}
                placeholder="HNB-SVC-001"
              />
            </label>

            <label style={labelStyle}>
              Duration (minutes)
              <input
                style={inputStyle}
                type="number"
                min={5}
                step={5}
                value={form.durationMinutes}
                onChange={updateField("durationMinutes")}
              />
            </label>

            <label style={labelStyle}>
              Price
              <input
                style={inputStyle}
                value={form.price}
                onChange={updateField("price")}
                placeholder="35.00"
              />
            </label>

            <label style={labelStyle}>
              Deposit required
              <select
                style={inputStyle}
                value={form.depositRequired}
                onChange={updateField("depositRequired")}
              >
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </select>
            </label>

            <label style={labelStyle}>
              Deposit amount
              <input
                style={inputStyle}
                value={form.depositAmount}
                onChange={updateField("depositAmount")}
                disabled={form.depositRequired !== "yes"}
                placeholder="10.00"
              />
            </label>
          </div>

          {form.serviceCategory ? (
            <p style={helperStyle}>
              Category and subcategory help HnB keep services searchable, reviewable,
              and consistent across onboarding, dashboard management, and staff approval.
            </p>
          ) : null}
        </section>

        {isComplianceSensitive ? (
          <section style={complianceHintStyle}>
            This service category is compliance-sensitive. Activation may later depend
            on additional verification, practitioner evidence, or policy checks.
          </section>
        ) : null}

        <section style={cardStyle}>
          <label style={labelStyle}>
            Internal notes
            <textarea
              style={{ ...inputStyle, minHeight: 120, resize: "vertical" }}
              value={form.notes}
              onChange={updateField("notes")}
              placeholder="Add service preparation notes, consultation rules, aftercare notes, or operational guidance."
            />
          </label>
        </section>

        <section style={cardStyle}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "1rem",
              flexWrap: "wrap",
            }}
          >
            <div style={{ color: "#475569" }}>
              {savedAt ? `Service saved at ${savedAt}` : "No save yet"}
            </div>
            <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
              <button
                type="submit"
                style={
                  createPricingItem.isPending
                    ? { ...buttonStyle, opacity: 0.5, cursor: "not-allowed" }
                    : buttonStyle
                }
                disabled={createPricingItem.isPending}
              >
                {createPricingItem.isPending ? "Saving..." : "Save service"}
              </button>
              <Link to="/provider/dashboard/services" style={secondaryButtonStyle}>
                Back to services
              </Link>
            </div>
          </div>
        </section>
      </form>
    </div>
  );
}